<script lang="ts">
  import { push } from "svelte-spa-router";
  import { signup, ApiError } from "../lib/api";
  import { setUser } from "../lib/stores/auth";

  export let params: { token?: string } = {};

  let username = "";
  let password = "";
  let displayName = "";
  let fieldError = "";
  let errorMsg = "";
  let submitting = false;

  // Mirror the backend's SignupBody constraints so users hear the real
  // rule before the request goes out instead of getting a generic 422.
  const USERNAME_RE = /^[a-z0-9_-]{3,30}$/;

  function parse422(body: string): string | null {
    try {
      const data = JSON.parse(body);
      const issues = data?.detail;
      if (!Array.isArray(issues) || issues.length === 0) return null;
      // Take the first issue, format as "field: msg"
      const first = issues[0];
      const field = Array.isArray(first.loc) ? first.loc[first.loc.length - 1] : 'request';
      return `${field}: ${first.msg || 'invalid'}`;
    } catch {
      return null;
    }
  }

  async function handleSubmit() {
    fieldError = "";
    errorMsg = "";
    if (!displayName.trim()) { fieldError = "Enter your name"; return; }
    if (!username) { fieldError = "Pick a username"; return; }
    if (!USERNAME_RE.test(username.toLowerCase())) {
      fieldError = "Username must be 3–30 characters: lowercase letters, digits, underscores or hyphens only (no spaces or periods).";
      return;
    }
    if (!password) { fieldError = "Pick a password"; return; }
    if (password.length < 12) { fieldError = "Password must be at least 12 characters"; return; }
    submitting = true;
    try {
      const user = await signup(params.token ?? "", username.toLowerCase(), password, displayName.trim() || undefined);
      setUser(user);
      push("/map");
    } catch (e) {
      if (e instanceof ApiError && (e.status === 410 || e.status === 404)) {
        errorMsg = "This invite link is no longer valid. Ask Lucas Coleman for a fresh one.";
      } else if (e instanceof ApiError && e.status === 409) {
        errorMsg = "That username is already taken — try a different one.";
      } else if (e instanceof ApiError && e.status === 422) {
        const detail = parse422(e.message);
        errorMsg = detail
          ? `Couldn't create account — ${detail}.`
          : "Couldn't create account — please check the fields and try again.";
      } else {
        errorMsg = "Signup failed. Please try again.";
      }
    } finally {
      submitting = false;
    }
  }
</script>

<main class="page">
  <aside class="hero">
    <div class="hero-inner">
      <span class="eyebrow">North Carolina · House District 51</span>
      <h1 class="hero-title">Welcome<span class="ampersand">.</span></h1>
      <p class="hero-sub">
        You've been invited to canvass for April Montgomery. Set up your account
        and you're ready to knock doors.
      </p>

      <svg class="mark" viewBox="0 0 240 100" aria-hidden="true">
        <defs>
          <linearGradient id="sun-g2" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="#F2C457"/>
            <stop offset="100%" stop-color="#C99326"/>
          </linearGradient>
        </defs>
        <circle cx="186" cy="46" r="14" fill="url(#sun-g2)"/>
        <path d="M0 78 Q 50 50 100 64 T 200 60 L 240 70 L 240 100 L 0 100 Z"
              fill="#2D7A4F" opacity="0.55"/>
        <path d="M0 92 Q 60 64 130 82 T 240 86 L 240 100 L 0 100 Z"
              fill="#1F5135"/>
      </svg>

      <p class="hero-quote">
        <span class="quote-mark">“</span>Listening. Leading.
        <em>Delivering.</em><span class="quote-mark">”</span>
      </p>
    </div>
    <div class="hero-foot">
      <span class="mono">canvass.v1</span>
      <span>·</span>
      <span>single-use invite</span>
    </div>
  </aside>

  <section class="form-side">
    <form class="form-card" on:submit|preventDefault={handleSubmit} autocomplete="on">
      <header class="form-head">
        <span class="eyebrow">Create account</span>
        <h2>One quick setup.</h2>
        <p class="form-sub">
          Your <strong>username</strong> is what you'll type to sign in.<br>
          Your <strong>display name</strong> is how other volunteers see you in the app.
        </p>
      </header>

      {#if errorMsg}
        <p role="alert" class="msg msg-error">{errorMsg}</p>
      {/if}
      {#if fieldError}
        <p class="msg msg-field">{fieldError}</p>
      {/if}

      <label class="field">
        <span class="field-label">Display name</span>
        <input
          type="text"
          bind:value={displayName}
          autocomplete="name"
          placeholder="e.g. John Doe"
        />
        <span class="field-hint">Your full name — shown next to your contacts.</span>
      </label>

      <label class="field">
        <span class="field-label">Username</span>
        <input
          type="text"
          bind:value={username}
          autocomplete="username"
          autocapitalize="off"
          spellcheck="false"
          placeholder="e.g. jdoe, john_doe, johnd"
        />
        <span class="field-hint">3–30 characters, lowercase. Letters, digits, _ and − only. Used to sign in.</span>
      </label>

      <label class="field">
        <span class="field-label">Password</span>
        <input
          type="password"
          bind:value={password}
          autocomplete="new-password"
          placeholder="At least 12 characters"
        />
        <span class="field-hint">Minimum 12 characters. A short phrase works well — easier to remember than a random string.</span>
      </label>

      <button type="submit" class="submit" disabled={submitting}>
        {#if submitting}
          <span class="spinner" aria-hidden="true"></span>
          Creating…
        {:else}
          Create my account →
        {/if}
      </button>

      <p class="legal">
        By creating an account you agree to keep voter data confidential. Don't share contact
        notes outside the campaign.
      </p>
    </form>
  </section>
</main>

<style>
  .page {
    display: grid;
    grid-template-columns: 1.1fr 1fr;
    min-height: 100dvh;
    background: var(--paper);
    color: var(--ink);
    overflow: hidden;
  }
  .hero {
    position: relative;
    background: var(--leaf);
    color: var(--paper);
    padding: var(--s-7) var(--s-7);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow: hidden;
    isolation: isolate;
  }
  .hero::before {
    content: '';
    position: absolute; inset: 0;
    background:
      radial-gradient(circle at 78% 22%, rgba(229, 178, 64, .22) 0%, transparent 38%),
      repeating-linear-gradient(0deg, transparent 0 31px, rgba(255, 255, 255, .04) 31px 32px);
    pointer-events: none; z-index: 0;
  }
  .hero::after {
    content: ''; position: absolute; top: 0; bottom: 0; right: 36px;
    width: 1px; background: rgba(229, 178, 64, .35); z-index: 0;
  }
  .hero-inner, .hero-foot { position: relative; z-index: 1; }
  .hero-inner { max-width: 520px; }
  .eyebrow { color: var(--sun-bright); display: inline-block; margin-bottom: var(--s-5); }
  .hero-title {
    font-family: var(--font-display);
    font-size: clamp(3rem, 9vw, 5.5rem);
    line-height: 0.95; font-weight: 500;
    margin: 0 0 var(--s-4); color: var(--paper);
  }
  .ampersand { color: var(--sun-bright); font-style: italic; }
  .hero-sub {
    font-size: 1.1rem; line-height: 1.5;
    color: rgba(247, 243, 234, 0.78);
    margin: 0 0 var(--s-6); max-width: 36ch;
  }
  .mark {
    width: 280px; max-width: 100%; height: auto;
    margin: var(--s-6) 0 var(--s-5) -8px;
    filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.18));
  }
  .hero-quote {
    font-family: var(--font-display); font-style: italic;
    font-size: 1.25rem; line-height: 1.4;
    color: rgba(247, 243, 234, 0.92);
    max-width: 28ch; margin: 0;
  }
  .hero-quote em { color: var(--sun-bright); font-style: italic; }
  .quote-mark {
    color: var(--sun-bright); font-size: 1.6em;
    line-height: 0; vertical-align: -0.18em;
  }
  .hero-foot {
    display: flex; gap: var(--s-3); align-items: baseline;
    color: rgba(247, 243, 234, 0.55);
    font-size: 0.78rem; letter-spacing: 0.05em;
  }

  .form-side {
    display: flex; align-items: center; justify-content: center;
    padding: var(--s-7) var(--s-5);
    background: var(--paper);
  }
  .form-card {
    width: 100%; max-width: 440px;
    display: flex; flex-direction: column; gap: var(--s-4);
  }
  .form-head { margin-bottom: var(--s-3); }
  .form-head .eyebrow { color: var(--leaf-bright); display: block; margin-bottom: var(--s-2); }
  .form-head h2 {
    font-size: 2.2rem; line-height: 1.1; font-weight: 600;
    margin: 0 0 var(--s-3); color: var(--ink);
  }
  .form-sub {
    color: var(--ink-soft); font-size: 0.95rem; line-height: 1.55;
    max-width: 42ch; margin: 0;
  }
  .form-sub strong { color: var(--ink); }

  .field { display: flex; flex-direction: column; gap: var(--s-2); }
  .field-label {
    font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.12em;
    font-weight: 600; color: var(--ink-soft);
  }
  .field-hint {
    font-size: 0.78rem; color: var(--ink-muted); line-height: 1.4;
    margin-top: -2px;
  }
  .field input {
    font-family: var(--font-body);
    font-size: 1rem; padding: 12px 14px;
    background: var(--field);
    border: 1px solid var(--rule);
    border-bottom: 2px solid var(--rule-strong);
    color: var(--ink);
    transition: border-color 0.12s, box-shadow 0.12s, background 0.12s;
  }
  .field input::placeholder { color: var(--ink-muted); }
  .field input:focus {
    outline: none;
    border-color: var(--leaf-bright);
    border-bottom-color: var(--leaf-bright);
    background: #fff;
    box-shadow: 0 0 0 3px rgba(45, 122, 79, 0.12);
  }

  .submit {
    position: relative;
    margin-top: var(--s-3);
    padding: 14px 20px;
    background: var(--ink); color: var(--paper);
    border: none; font-family: var(--font-body);
    font-weight: 600; font-size: 0.95rem;
    letter-spacing: 0.02em; cursor: pointer;
    display: inline-flex; align-items: center;
    justify-content: center; gap: var(--s-2);
    transition: background 0.15s, transform 0.05s;
  }
  .submit:hover:not(:disabled) { background: var(--leaf); }
  .submit:active:not(:disabled) { transform: translateY(1px); }
  .submit:disabled { opacity: 0.6; cursor: progress; }
  .spinner {
    width: 12px; height: 12px;
    border: 2px solid rgba(247, 243, 234, 0.3);
    border-top-color: var(--paper);
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  .msg { margin: 0; padding: 12px 14px; font-size: 0.9rem; border-left: 3px solid; }
  .msg-error { background: var(--alert-soft); border-color: var(--alert); color: var(--alert); }
  .msg-field { background: var(--sun-soft); border-color: var(--sun); color: var(--ink); }

  .legal {
    font-size: 0.8rem; color: var(--ink-muted); line-height: 1.5;
    margin: var(--s-4) 0 0; padding-top: var(--s-4);
    border-top: 1px dashed var(--rule);
  }

  @media (max-width: 880px) {
    .page { grid-template-columns: 1fr; }
    .hero { padding: var(--s-6) var(--s-5); min-height: 280px; }
    .hero-title { font-size: clamp(2.5rem, 12vw, 4rem); }
    .mark { width: 220px; }
    .form-side { padding: var(--s-6) var(--s-4); }
  }
  @media (max-width: 500px) {
    .hero { min-height: auto; padding: var(--s-5) var(--s-4); }
    .mark { display: none; }
    .hero-quote { font-size: 1.05rem; }
  }
</style>
